import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { submitComplaint, getComplianceConfig } from '../../services/complianceService';
import toast from 'react-hot-toast';
import BackButtonBottom from '../../components/common/BackButtonBottom';

export default function SubmitComplaint() {
  const navigate = useNavigate();
  const location = useLocation();
  // Optional: pre-fill from a booking link
  const prefilledBookingId = location.state?.bookingId || null;
  const prefilledBookingRef = location.state?.bookingReferenceNumber || null;
  const prefilledDriverId = location.state?.driverId || null;

  const [categories, setCategories] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    category: '',
    description: '',
    desiredOutcome: '',
    bookingId: prefilledBookingId,
    bookingReferenceNumber: prefilledBookingRef,
    againstDriverId: prefilledDriverId,
  });

  useEffect(() => {
    getComplianceConfig()
      .then((c) => setCategories(c.complaintCategories || {}))
      .catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!form.category) { toast.error('Choose a category'); return; }
    if (!form.description || form.description.trim().length < 20) {
      toast.error('Description must be at least 20 characters');
      return;
    }
    setSubmitting(true);
    try {
      const result = await submitComplaint(form);
      toast.success(`Complaint ${result.referenceNumber} submitted. We'll respond within 24 hours.`);
      navigate('/rider/complaints');
    } catch (err) {
      toast.error(err.message || 'Submission failed');
    }
    setSubmitting(false);
  };

  return (
    <div className="max-w-[700px] mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate(-1)} className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">← Back</button>
      </div>

      <h1 className="font-serif mt-0 mb-2">Submit a complaint</h1>
      <p className="text-body text-[0.9rem] mb-6">
        Complaints are formally recorded, investigated, and answered. Use this for serious concerns —
        for general questions, use Support instead.
      </p>

      {prefilledBookingRef && (
        <div className="p-3 rounded-xl bg-slate-50 mb-4 text-[0.85rem] text-body">
          Linked to booking: <strong>{prefilledBookingRef}</strong>
        </div>
      )}

      <div className="form-group">
        <label className="form-label">Category *</label>
        <select className="form-input form-select" value={form.category}
          onChange={(e) => setForm({ ...form, category: e.target.value })}>
          <option value="">Select a category...</option>
          {Object.entries(categories).map(([key, cfg]) => (
            <option key={key} value={key}>{cfg.label}</option>
          ))}
        </select>
      </div>

      <div className="form-group">
        <label className="form-label">Describe what happened * (min 20 chars)</label>
        <textarea className="form-input" rows={6}
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder="Tell us what happened, when, where, and who was involved..."
        />
        <div className="text-[0.75rem] text-muted mt-1">
          {form.description.length} characters
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">What outcome are you hoping for? (optional)</label>
        <textarea className="form-input" rows={3}
          value={form.desiredOutcome}
          onChange={(e) => setForm({ ...form, desiredOutcome: e.target.value })}
          placeholder="e.g. refund, apology, driver investigated, etc."
        />
      </div>

      <div className="p-3.5 rounded-xl mb-4 text-[0.82rem] leading-normal" style={{ background: '#fef3c7', border: '1px solid #fde68a', color: '#92400e' }}>
        <strong>What happens next:</strong> Reservo acknowledges your complaint within 24 hours,
        provides an initial response within 7 days, and aims to resolve within 28 days.
        Serious complaints may be referred to the NSW Point to Point Transport Commissioner.
      </div>

      <button onClick={handleSubmit} disabled={submitting} className="w-full p-3.5 rounded-xl border-0 text-white cursor-pointer font-bold text-[1rem]"
        style={{ background: '#ff6b6b' }}>
        {submitting ? 'Submitting...' : 'Submit complaint'}
      </button>
    <BackButtonBottom label="Back" />
    </div>
  );
}